import React from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Pause, Play, PlayCircle, X } from 'lucide-react';

interface ScheduleBulkActionsBarProps {
  count: number;
  /** How many of the selected schedules are currently enabled. */
  activeCount: number;
  onPause: () => void;
  onResume: () => void;
  onRunNow: () => void;
  onClear: () => void;
}

/**
 * Appears only when rows are selected.
 *
 * Pause and Resume are offered separately rather than as one toggle: a mixed
 * selection has no sensible "flip" semantics, and during an incident the
 * intent is always explicit — stop these, or start these.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleBulkActionsBar: React.FC<ScheduleBulkActionsBarProps> = ({
  count,
  activeCount,
  onPause,
  onResume,
  onRunNow,
  onClear
}) => {
  if (count === 0) return null;

  const pausedCount = count - activeCount;

  return (
    <div className='flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-2'>
      <span className='text-xs font-medium'>
        {count} selected
        {activeCount > 0 && pausedCount > 0 && (
          <span className='ml-1 font-normal text-muted-foreground'>
            ({activeCount} active, {pausedCount} paused)
          </span>
        )}
      </span>

      <div className='ml-auto flex flex-wrap items-center gap-1'>
        <Button
          variant='outline'
          size='sm'
          className='h-7 text-xs'
          onClick={onPause}
          disabled={activeCount === 0}
          title={activeCount === 0 ? 'Nothing selected is running' : undefined}
        >
          <Pause className='mr-1 h-3 w-3' />
          Pause
        </Button>
        <Button
          variant='outline'
          size='sm'
          className='h-7 text-xs'
          onClick={onResume}
          disabled={pausedCount === 0}
          title={pausedCount === 0 ? 'Nothing selected is paused' : undefined}
        >
          <Play className='mr-1 h-3 w-3' />
          Resume
        </Button>
        <Button variant='outline' size='sm' className='h-7 text-xs' onClick={onRunNow}>
          <PlayCircle className='mr-1 h-3 w-3' />
          Run now
        </Button>
        <Button variant='ghost' size='sm' className='h-7 text-xs' onClick={onClear}>
          <X className='mr-1 h-3 w-3' />
          Clear
        </Button>
      </div>
    </div>
  );
};
